import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import type { IPaginationProps } from '@occc/ui-design-system';
import { usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useDriveConversationsQuery, DriveConversationFragment } from '@occc/graphql';

import { truncateText } from '../../utils/text-utils';
import type { ConversationListItem } from '../chat-history.types';
import {
  CHAT_HISTORY_PAGE_SIZE,
  CHAT_HISTORY_AFTER_PARAM,
  CHAT_HISTORY_BEFORE_PARAM,
  CHAT_HISTORY_TRUNCATE_LENGTH,
} from '../chat-history.constants';

type UseChatHistoryResult = {
  conversations: ConversationListItem[] | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  paginationProps: IPaginationProps;
};

const mapConversationToListItem = (
  conversation: DriveConversationFragment,
): ConversationListItem => {
  const firstPrompt = conversation.prompts[0];
  const name = firstPrompt
    ? truncateText(firstPrompt.content, CHAT_HISTORY_TRUNCATE_LENGTH)
    : 'Untitled';

  return {
    id: conversation.id,
    name,
    createdAt: conversation.createdAt,
  };
};

export const useChatHistory = (): UseChatHistoryResult => {
  const [paginationInfo, onPageChange] = usePaginationParams({
    pageSize: CHAT_HISTORY_PAGE_SIZE,
    afterParamName: CHAT_HISTORY_AFTER_PARAM,
    beforeParamName: CHAT_HISTORY_BEFORE_PARAM,
  });

  const { data, loading, error, paginationProps } = usePaginatedQuery(
    useDriveConversationsQuery,
    'driveConversations.pageInfo',
    CHAT_HISTORY_PAGE_SIZE,
    {
      fetchPolicy: 'cache-and-network',
    },
    paginationInfo,
    onPageChange,
  );

  const conversations = useMemo<ConversationListItem[] | undefined>(
    () => data?.driveConversations.edges.map((edge) => mapConversationToListItem(edge.node)),
    [data],
  );

  return {
    conversations,
    loading,
    error,
    paginationProps,
  };
};
